<template>
    <TailwindLayout title="Dispute Evidence">
        <div class="bg-white rounded-lg shadow-sm -mx -mt-10">
            <!-- Header: title + action button -->
            <div class="px-6 pt-6">
                <div class="flex items-start justify-between mb-4">
                    <h1 class="font-semibold text-2xl leading-tight text-gray-900">
                        Evidence for charge
                        <span class="font-mono text-base">{{ charge_id }}</span>
                    </h1>
                    <a
                        :href="route('bridge.disputes.evidence-pdf', charge_id)"
                        class="primaryButton"
                    >
                        Download Evidence PDF
                    </a>
                </div>
            </div>

            <div class="px-6 py-6 space-y-6">
                <!-- FCRA notice -->
                <div class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                    <p class="text-sm text-yellow-800">
                        <strong>FCRA notice:</strong> This page intentionally excludes credit-report contents,
                        score, tradelines, and SSN. Do not upload, export, or share anything beyond what you
                        see here with Stripe or the issuing bank.
                    </p>
                </div>

                <!-- Charge -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Charge</h3>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <dt class="text-gray-500">Stripe Charge ID</dt>
                        <dd class="font-mono">{{ evidence.charge.id }}</dd>
                        <dt class="text-gray-500">Invoice ID</dt>
                        <dd>{{ evidence.charge.invoice_id }}</dd>
                        <dt class="text-gray-500">Amount Charged</dt>
                        <dd>${{ evidence.charge.charged_amount }}</dd>
                        <dt class="text-gray-500">Charged At</dt>
                        <dd>{{ evidence.charge.created_at }}</dd>
                    </dl>
                </section>

                <!-- Applicant -->
                <section v-if="evidence.applicant" class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Applicant</h3>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <dt class="text-gray-500">Name</dt>
                        <dd>{{ evidence.applicant.name }}</dd>
                        <dt class="text-gray-500">Email</dt>
                        <dd>{{ evidence.applicant.email }}</dd>
                        <dt class="text-gray-500">User ID</dt>
                        <dd>{{ evidence.applicant.id }}</dd>
                    </dl>
                </section>

                <!-- Property -->
                <section v-if="evidence.property" class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Property</h3>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <dt class="text-gray-500">Address</dt>
                        <dd>{{ evidence.property.full_address }}</dd>
                    </dl>
                </section>

                <!-- Service rendered -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Service Rendered</h3>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <dt class="text-gray-500">Report generated</dt>
                        <dd>{{ evidence.application.report_generated ? 'Yes' : 'No' }}<span v-if="evidence.application.experian_transaction_id"> (Experian txn {{ evidence.application.experian_transaction_id }})</span></dd>
                        <dt class="text-gray-500">Report shared with landlord</dt>
                        <dd>{{ evidence.application.is_shared ? 'Yes' : 'No' }}<span v-if="evidence.application.share_id"> (Experian share ID {{ evidence.application.share_id }})</span></dd>
                        <dt class="text-gray-500">Application completed at</dt>
                        <dd>{{ evidence.application.completed_at || 'Not completed' }}</dd>
                        <dt class="text-gray-500">Applicant IP at submission</dt>
                        <dd class="font-mono">{{ evidence.application.user_ip || '—' }}</dd>
                    </dl>
                </section>

                <!-- Consent audit trail -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Consent Audit Trail</h3>
                    <table class="min-w-full divide-y divide-gray-200 text-sm">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accepted At</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">User Agent</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">ToS Ver.</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Signed?</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-if="evidence.consents.length === 0">
                                <td colspan="6" class="px-3 py-4 text-sm text-gray-500 text-center">No consent events logged (applies to applications prior to this feature).</td>
                            </tr>
                            <tr v-for="(c, i) in evidence.consents" :key="i">
                                <td class="px-3 py-2">{{ c.consent_type }}</td>
                                <td class="px-3 py-2">{{ c.accepted_at }}</td>
                                <td class="px-3 py-2 font-mono">{{ c.ip_address || '—' }}</td>
                                <td class="px-3 py-2 truncate max-w-xs" :title="c.user_agent">{{ c.user_agent || '—' }}</td>
                                <td class="px-3 py-2">{{ c.tos_version || '—' }}</td>
                                <td class="px-3 py-2">{{ c.has_signature ? 'Yes' : 'No' }}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>

                <!-- Signatures -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Signatures on File</h3>
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <h4 class="text-sm font-medium mb-1">Terms of Service</h4>
                            <img v-if="evidence.application.tos_signature" :src="evidence.application.tos_signature" class="border rounded bg-white" />
                            <p v-else class="text-sm text-gray-400 italic">Not captured (applicant predates feature)</p>
                        </div>
                        <div>
                            <h4 class="text-sm font-medium mb-1">FCRA Authorization</h4>
                            <img v-if="evidence.application.authorization_signature" :src="evidence.application.authorization_signature" class="border rounded bg-white" />
                            <p v-else class="text-sm text-gray-400 italic">Not captured</p>
                        </div>
                        <div>
                            <h4 class="text-sm font-medium mb-1">Background Report Authorization</h4>
                            <img v-if="evidence.application.background_report_authorization_signature" :src="evidence.application.background_report_authorization_signature" class="border rounded bg-white" />
                            <p v-else class="text-sm text-gray-400 italic">Not captured</p>
                        </div>
                    </div>
                </section>

                <!-- Acknowledgments -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Additional Acknowledgments</h3>
                    <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                        <dt class="text-gray-500">SSN acknowledgment</dt>
                        <dd>{{ evidence.application.applicant_ssn_acknowledgment ? 'Yes' : 'No' }}</dd>
                        <dt class="text-gray-500">No-refund acknowledgment</dt>
                        <dd>{{ evidence.application.no_refund_acknowledgment ? 'Yes' : 'No' }}</dd>
                        <dt class="text-gray-500">Background check approved</dt>
                        <dd>
                            {{ evidence.application.tenant_approves_criminal_background ? 'Yes' : 'No' }}
                            <span v-if="evidence.application.tenant_approves_criminal_background_datetime"> — {{ evidence.application.tenant_approves_criminal_background_datetime }}</span>
                        </dd>
                    </dl>
                </section>

                <!-- Refund Policy Acknowledgment (modal text shown to applicant) -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Refund Policy Acknowledgment</h3>
                    <p class="text-sm text-gray-500 mb-3">
                        Text shown to the applicant in a modal dialog during the application flow.
                        Applicant must click <strong>"I Accept"</strong> to proceed; the click is recorded
                        with timestamp, IP, and user agent in the Consent Audit Trail.
                    </p>
                    <div class="border-2 border-red-300 bg-red-50 rounded-md p-4">
                        <p class="text-xs uppercase tracking-wide font-bold text-red-700 mb-2">
                            Acknowledged: {{ evidence.refund_policy_acknowledgment.acknowledged ? 'Yes' : 'No' }}
                        </p>
                        <p class="text-sm text-gray-800 leading-relaxed">{{ evidence.refund_policy_acknowledgment.text }}</p>
                    </div>
                </section>

                <!-- Checkout Purchase Acknowledgment (checkbox + signature beside Purchase) -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Checkout Purchase Acknowledgment</h3>
                    <p class="text-sm text-gray-500 mb-3">
                        Checkbox shown next to the Purchase button. Applicant must tick it and sign before
                        the card is charged; each attempt is recorded with timestamp, IP, user agent and
                        signature in the Consent Audit Trail.
                    </p>
                    <div class="border-2 border-red-300 bg-red-50 rounded-md p-4">
                        <p class="text-xs uppercase tracking-wide font-bold text-red-700 mb-2">
                            Acknowledged: {{ evidence.purchase_acknowledgment.acknowledged ? 'Yes' : 'No' }}
                        </p>
                        <p class="text-sm text-gray-800 leading-relaxed">{{ evidence.purchase_acknowledgment.text }}</p>
                    </div>
                </section>

                <!-- Application Boost Purchase Acknowledgment (landlord paid the application fee) -->
                <section v-if="evidence.boost_purchase_acknowledgment?.acknowledged" class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Application Boost Purchase Acknowledgment</h3>
                    <p class="text-sm text-gray-500 mb-3">
                        Checkbox shown next to the Purchase button on the Application Boost checkout, used when
                        the landlord paid the application fee. Applicant must tick it and sign before the card is
                        charged; each attempt is recorded in the Consent Audit Trail.
                    </p>
                    <div class="border-2 border-red-300 bg-red-50 rounded-md p-4">
                        <p class="text-xs uppercase tracking-wide font-bold text-red-700 mb-2">
                            Acknowledged: Yes
                        </p>
                        <p class="text-sm text-gray-800 leading-relaxed">{{ evidence.boost_purchase_acknowledgment.text }}</p>
                    </div>
                </section>

                <!-- Terms of Service document (signed by applicant) -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Terms of Service Document</h3>
                    <p class="text-sm text-gray-500 mb-3">
                        Version <strong class="font-mono">{{ evidence.terms_of_service.loaded_version }}</strong>
                        <template v-if="evidence.terms_of_service.fell_back">
                            — applicant's recorded version was
                            <strong class="font-mono">{{ evidence.terms_of_service.requested_version || 'not recorded' }}</strong>;
                            this fallback shows the configured current version (legacy application).
                        </template>
                    </p>
                    <div v-if="evidence.terms_of_service.fell_back" class="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3 text-sm text-yellow-800">
                        <strong>Note:</strong> The exact version this applicant accepted is not on file.
                        The document below reflects the version in effect at the time of charge.
                    </div>
                    <div class="prose prose-sm max-w-none border border-gray-200 rounded-md p-4 bg-gray-50 max-h-[600px] overflow-y-auto" v-html="evidence.terms_of_service.html"></div>
                </section>

                <!-- Email timeline -->
                <section class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-3">Email Timeline</h3>
                    <table class="min-w-full divide-y divide-gray-200 text-sm">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Mail Class</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            <tr v-if="evidence.email_logs.length === 0">
                                <td colspan="4" class="px-3 py-4 text-sm text-gray-500 text-center">No email logs found.</td>
                            </tr>
                            <tr v-for="log in evidence.email_logs" :key="log.id">
                                <td class="px-3 py-2 truncate max-w-xs" :title="log.mail_class">{{ formatMailClass(log.mail_class) }}</td>
                                <td class="px-3 py-2">{{ formatRecipients(log.recipients) }}</td>
                                <td class="px-3 py-2">{{ log.status }}</td>
                                <td class="px-3 py-2">{{ log.sent_at || log.created_at }}</td>
                            </tr>
                        </tbody>
                    </table>
                </section>
            </div>
        </div>
    </TailwindLayout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';

export default {
    components: { TailwindLayout },
    props: {
        charge_id: { type: String, required: true },
        evidence: { type: Object, required: true },
    },
    methods: {
        formatRecipients(recipients) {
            if (recipients === null || recipients === undefined) {
                return '—';
            }
            if (typeof recipients === 'string') {
                return recipients;
            }
            if (Array.isArray(recipients)) {
                return recipients
                    .map(r => (typeof r === 'string' ? r : r?.email ?? r?.address ?? r?.name ?? JSON.stringify(r)))
                    .filter(Boolean)
                    .join(', ');
            }
            // Plain object — pull out the typical address shape.
            return recipients.email ?? recipients.address ?? recipients.name ?? JSON.stringify(recipients);
        },
        formatMailClass(mailClass) {
            if (!mailClass || mailClass === 'Unknown') {
                return mailClass ?? '—';
            }
            // Trim "App\\Mail\\" prefix for readability.
            return mailClass.replace(/^App\\\\Mail\\\\/, '').replace(/^App\\Mail\\/, '');
        },
    },
};
</script>
